import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnvPrefix } from '../hooks/useEnvNavigation';
import { useAuth } from '../contexts/AuthContext';
import { sopsAPI } from '../services/api';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Search,
  ChevronRight,
  Loader2,
  FileText,
  Plus,
  X,
  Trash2,
  Truck,
  Users,
  BarChart3
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const categoryIcons = {
  fleet: Truck,
  driver: Users,
  management: BarChart3,
};

const categoryColors = {
  fleet: 'bg-blue-100 text-blue-700',
  driver: 'bg-emerald-100 text-emerald-700',
  management: 'bg-purple-100 text-purple-700',
};

export const SOPsPage = () => {
  const navigate = useNavigate();
  const envPrefix = useEnvPrefix();
  const { isAdmin } = useAuth();
  const [sops, setSOPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '',
    title: '',
    category: 'fleet',
    description: '',
    content: '',
  });

  const fetchSOPs = async () => {
    try {
      setLoading(true);
      const response = await sopsAPI.list();
      setSOPs(response.data);
    } catch (error) {
      console.error('Failed to fetch SOPs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSOPs(); }, []);

  const filteredSOPs = sops.filter(sop => {
    const matchesSearch = sop.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sop.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         sop.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || sop.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedSOPs = filteredSOPs.reduce((acc, sop) => {
    const category = sop.category;
    if (!acc[category]) acc[category] = [];
    acc[category].push(sop);
    return acc;
  }, {});

  const handleAddSOP = async () => {
    if (!form.code || !form.title || !form.description) {
      toast.error('Please fill in Code, Title, and Description');
      return;
    }
    setSaving(true);
    try {
      const reviewDate = new Date();
      reviewDate.setFullYear(reviewDate.getFullYear() + 1);
      await sopsAPI.create({
        code: form.code.toUpperCase(),
        title: form.title,
        category: form.category,
        description: form.description,
        content: form.content || `Standard Operating Procedure for ${form.title}`,
        review_date: reviewDate.toISOString(),
      });
      toast.success(`SOP ${form.code.toUpperCase()} created`);
      setShowAddModal(false);
      setForm({ code: '', title: '', category: 'fleet', description: '', content: '' });
      await fetchSOPs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create SOP');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSOP = async (e, sopCode, sopTitle) => {
    e.stopPropagation();
    if (!window.confirm(`Delete SOP ${sopCode}: ${sopTitle}?`)) return;
    try {
      await sopsAPI.delete(sopCode);
      toast.success(`SOP ${sopCode} deleted`);
      await fetchSOPs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete SOP');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sops-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            SOP LIBRARY
          </h1>
          <p className="text-slate-500 mt-1">
            Standard Operating Procedures with version control
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowAddModal(true)} data-testid="add-sop-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add SOP
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search SOPs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
          data-testid="sops-search"
        />
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="w-full justify-start bg-white border border-slate-200 p-1 h-auto flex-wrap">
          <TabsTrigger 
            value="all"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
          >
            All
          </TabsTrigger>
          <TabsTrigger 
            value="fleet"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
          >
            <Truck className="w-4 h-4 mr-2" />
            Fleet
          </TabsTrigger>
          <TabsTrigger 
            value="driver"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
          >
            <Users className="w-4 h-4 mr-2" />
            Driver
          </TabsTrigger>
          <TabsTrigger 
            value="management"
            className="data-[state=active]:bg-slate-900 data-[state=active]:text-white px-4 py-2"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Management
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* SOPs List */}
      {filteredSOPs.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No SOPs found</h3>
          <p className="text-slate-500 mt-1">
            {sops.length === 0 ? 'Get started by adding your first SOP' : 'Try adjusting your search criteria'}
          </p>
          {sops.length === 0 && isAdmin && (
            <Button className="mt-4" onClick={() => setShowAddModal(true)} data-testid="add-sop-empty-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add SOP
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedSOPs).map(([category, categorySops]) => {
            const Icon = categoryIcons[category] || FileText;
            
            return (
              <Card key={category}>
                <CardContent className="p-0">
                  <Accordion type="single" collapsible defaultValue={category}>
                    <AccordionItem value={category} className="border-0">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${categoryColors[category]}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span 
                            className="font-bold uppercase tracking-wide"
                            style={{ fontFamily: 'Barlow Condensed' }}
                          >
                            {category} SOPs ({categorySops.length})
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="border-t border-slate-100">
                          {categorySops.map((sop) => (
                            <div
                              key={sop.id}
                              className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                              onClick={() => navigate(`${envPrefix}/sops/${sop.code}`)}
                              data-testid={`sop-item-${sop.code}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-sm font-bold text-slate-500">
                                    {sop.code}
                                  </span>
                                  {sop.current_version && (
                                    <Badge variant="outline" className="text-xs">
                                      v{sop.current_version}
                                    </Badge>
                                  )}
                                </div>
                                <h3 className="font-semibold text-slate-900">{sop.title}</h3>
                                <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                                  {sop.description}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                {isAdmin && (
                                  <button
                                    onClick={(e) => handleDeleteSOP(e, sop.code, sop.title)}
                                    className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                    title="Delete SOP"
                                    data-testid={`delete-sop-${sop.code}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add SOP Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-sm border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed' }}>
                ADD NEW SOP
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    SOP Code *
                  </label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value })}
                    placeholder="e.g. F001"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="add-sop-code"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    Category *
                  </label>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="add-sop-category"
                  >
                    <option value="fleet">Fleet</option>
                    <option value="driver">Driver</option>
                    <option value="management">Management</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Vehicle Daily Inspection Procedure"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="add-sop-title"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Description *
                </label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Brief description of this SOP"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-y"
                  data-testid="add-sop-description"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Initial Content (optional)
                </label>
                <textarea
                  value={form.content}
                  onChange={e => setForm({ ...form, content: e.target.value })}
                  rows={4}
                  placeholder="SOP content for the first version"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900 resize-y"
                  data-testid="add-sop-content"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
              <Button variant="outline" onClick={() => setShowAddModal(false)} data-testid="add-sop-cancel">
                Cancel
              </Button>
              <Button onClick={handleAddSOP} disabled={saving} data-testid="add-sop-save">
                {saving ? 'Creating...' : 'Create SOP'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPsPage;
